<template>
    <v-navigation-drawer :rail="rail" class="sidebar-branded pa-0" expand-on-hover
                         @update:rail="(val) => isCollapsed = val">
        <v-list class="pa-0">
            <v-list-item class="pa-0">
                <template #title>
                    <div class="d-flex align-center w-100">
                        <template v-if="isCollapsed">
                            <UserAvatar class="user-initials-avatar mx-auto" :image="userAvatar"
                                        :name="userName" :size="36"/>
                        </template>
                        <template v-else>
                            <UserAvatar class="user-initials-avatar mr-2" :image="userAvatar"
                                        :name="userName" :size="36"/>
                            <div class="text-left">
                                <h5 class="text-white" style="font-size: 13px; line-height: 1.3">{{ userName }}</h5>
                                <span class="text-caption" style="color: rgba(255,255,255,0.6); font-size: 11px">{{ userEmail }}</span>
                            </div>
                        </template>
                    </div>
                </template>
            </v-list-item>
        </v-list>

        <v-toolbar class="bg-transparent sidebar-toggle-toolbar" height="30px">
            <v-btn :class="{'active':!rail}"
                   icon="mdi-menu"
                   @click="()=> {rail = !rail}"/>
        </v-toolbar>

        <v-list class="mt-1 text-left" density="compact">
            <template v-for="(menuItem, index) in menuItems" :key="menuItem.key ?? index">
                <v-divider v-if="menuItem.divider" class="my-2 mx-3"/>

                <v-list-group v-else-if="menuItem.submenu" :value="index">
                    <template v-slot:activator="{ props }">
                        <v-list-item :prepend-icon="menuItem.icon" :title="menuItem.title" v-bind="props"
                                     @click="navigateTo(menuItem.url,menuItem.params??{})"/>
                    </template>

                    <template v-for="(submenuItem, subIndex) in menuItem.submenu">
                        <v-list-group v-if="submenuItem.submenu" :value="subIndex">
                            <template v-slot:activator="{ props }">
                                <v-list-item :title="submenuItem.title"
                                             v-bind="props"
                                             @click="navigateTo(submenuItem.url,submenuItem.params??{})"/>
                            </template>
                            <v-list-item v-for="(submenuSubItem) in submenuItem.submenu" :title="submenuSubItem.title"
                                         @click="navigateTo(submenuSubItem.url,submenuSubItem.params??{})"/>
                        </v-list-group>

                        <v-list-item v-else :title="submenuItem.title"
                                     @click="navigateTo(submenuItem.url,submenuItem.params??{})"/>
                    </template>
                </v-list-group>

                <v-list-item v-else :prepend-icon="menuItem.icon" :title="menuItem.title" :value="index"
                             :append-icon="menuItem.locked ? 'mdi-lock-outline' : undefined"
                             class="text-left" :class="{ 'menu-locked': menuItem.locked }"
                             @click="menuItem.locked ? goToPlans() : navigateTo(menuItem.url, menuItem.params ?? {})"/>
            </template>
        </v-list>

        <template v-slot:append>
            <v-list>
                <v-list-item to="/" class="d-flex justify-center pa-3">
                    <v-avatar v-if="isCollapsed" :image="logoWhite" size="32"/>
                    <div v-else class="d-flex align-center justify-center">
                        <v-img :src="logoFullWhite" :width="110" contain class="flex-grow-0"/>
                        <span class="text-white font-weight-bold font-italic text-h6 ms-1">Go</span>
                    </div>
                </v-list-item>
            </v-list>
        </template>
    </v-navigation-drawer>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import logoWhite from "@/assets/images/logo-white.png";
import logoFullWhite from "@/assets/images/logo-full-white.png";
import {useAuthStore} from "@/stores/authStore";
import {useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {send} from "@/composables/apiClient";
import UserAvatar from "@/components/UserAvatar.vue";

defineEmits(['update:modelValue']);
const {showSnackbar} = useSnackbarStore();
const {navigateTo} = useNavigation();

// A locked menu item used to navigate straight to `plans`, which is ownerOnly -
// so for a non-owner member on a free circle, the most-clicked route to that
// screen ended in a silent bounce. Tell them instead.
const goToPlans = () => {
    if (!authStore.isCircleOwner) {
        showSnackbar('Ask your circle owner to upgrade the plan.', 'info', 5000);

        return;
    }
    navigateTo('plans', {});
};
const authStore = useAuthStore();
const props = defineProps({
    modelValue: Boolean
});

// Pin the sidebar open by default for new sessions. Only collapse it when the
// user has explicitly turned "keep sidebar visible" off (sidebar === '0'/0).
// Read once on purpose: it is the starting state, and the user's own toggle
// takes over from there.
const sidebarPref = authStore.configs?.sidebar;
const hasSidebarPref = sidebarPref !== undefined && sidebarPref !== null && sidebarPref !== '';
const rail = ref(hasSidebarPref ? !parseInt(sidebarPref) : false);
const isCollapsed = ref(rail.value);

const companyLogo = ref(authStore?.user?.company?.company_logo);
const userEmail = computed(() => authStore?.user?.email ?? '');

const userName = computed(() => {
    const first = authStore?.user?.first_name ?? '';
    const last = authStore?.user?.last_name ?? '';
    return `${first} ${last}`.trim();
});
const userAvatar = computed(() => authStore?.user?.avatar ?? '');

// Premium (active paid circle plan) gates the Mileage Log item - greyed + locked
// until subscribed. Sourced from entitlements.is_premium on GET /go-subscription.
const isPremium = ref(false);
onMounted(async () => {
    try {
        const {data} = await send("get", "go-subscription");
        isPremium.value = data?.data?.entitlements?.is_premium === true;
    } catch (e) {
        // Leave locked on failure.
    }
});

// Ordered for a consumer/family mental model (like Life360 / Find My): lead with
// the live map, then the unified look-back surface (Activity = trips + alerts in
// one feed), then the people ("Circle"), Places and their Alerts (setup).
// Everything below the divider is management (Trackers, Account) and is
// intentionally de-emphasised.
const sidebarMenuItems = [
    {
        icon: "mdi-radar",
        title: "Live View",
        url: "dashboard",
        key: "dashboard"
    },
    {
        icon: "mdi-timeline-text-outline",
        title: "Activity",
        url: "activity",
        key: "activity"
    },
    {
        icon: "mdi-book-open-variant",
        title: "Mileage Log",
        url: "mileage",
        key: "mileage"
    },
    {
        icon: "mdi-account-multiple",
        title: "Circle",
        url: "members",
        key: "members"
    },
    {
        url: "places",
        key: "places",
        title: "Places",
        icon: "mdi-map-marker-multiple-outline"
    },
    {
        url: "monitoring",
        key: "monitoring",
        title: "Speeding Rules",
        icon: "mdi-speedometer"
    },
    {
        divider: true,
        key: "__manage_divider"
    },
    {
        url: "devices",
        key: "devices",
        title: "Trackers",
        icon: "mdi-devices"
    },
    {
        url: "",
        key: "account",
        title: "Account",
        icon: "mdi-account",
        submenu: [
            {
                url: "plans",
                title: "Plans & Upgrade",
            },
            {
                url: "invoices",
                title: "Invoices",
            },
            {
                url: "payment-methods",
                title: "Payment Methods",
            },
            {
                url: "profile",
                title: "Profile",
            },
            {
                url: "settings",
                title: "Settings",
            },
            {
                url: "logout",
                title: "Logout",
            }
        ]
    },
];

const menuItems = computed(() => {
    const isMinor = authStore.isMinor;
    // One source of truth. This used to re-derive ownership independently of
    // the store getter, so the two could disagree - showing a menu item the
    // router then bounces, or hiding one from a genuine owner.
    const isCircleOwner = authStore.isCircleOwner;

    // Structural dividers plus the always-on daily items (Circle + the two map
    // features) are never hidden by the server menus payload; the Circle page is
    // itself role/age-aware (owner manages; members view + see invitations; minors
    // get members + invitations only). Everything else appears only when its key
    // is present in the menus payload. Read through the store, not a copy taken
    // at setup: the menus change on a circle switch or session refresh, and a
    // copy would keep showing the old circle's items.
    const menus = authStore.menus;
    let items = menus
        ? sidebarMenuItems.filter(item => item.divider
            || ['activity', 'places', 'members', 'mileage'].includes(item.key)
            || Object.keys(menus).includes(item.key))
        : sidebarMenuItems;
    // Clone so recomputes don't mutate the source array/submenus.
    items = items.map(i => ({...i, submenu: i.submenu ? i.submenu.map(s => ({...s})) : undefined}));

    // Minors can't manage trackers, and the mileage log (a tax tool) is not for
    // minors either.
    if (isMinor) {
        items = items.filter(i => i.key !== 'devices' && i.key !== 'mileage');
    }

    // Billing is owner-only and never for minors.
    items.forEach((item) => {
        if (item.title !== 'Account') return;
        if (!isCircleOwner || isMinor) {
            item.submenu = item.submenu.filter((s) => !['plans', 'invoices', 'payment-methods'].includes(s.url));
        }
    });

    // Plans, but not the rest of Account, for a circle with nothing to buy.
    // Invoices and Payment Methods stay: both are account management rather than
    // a purchase, and a comp that ends leaves a circle needing a card on file.
    if (authStore.noPurchaseAvailable) {
        items.forEach((item) => {
            if (item.title !== 'Account' || !item.submenu) return;
            item.submenu = item.submenu.filter((s) => s.url !== 'plans');
        });
    }

    // Mileage Log is premium-gated: greyed + locked until the circle is on a paid
    // plan (tapping then routes to Plans).
    items.forEach((i) => {
        if (i.key === 'mileage') i.locked = !isPremium.value;
    });

    // Drop a trailing/leading divider so a filtered-out neighbour never leaves a
    // stray line at the very top or bottom of the list.
    items = items.filter((item, i) => !item.divider || (i > 0 && i < items.length - 1));
    return items;
});
</script>

<style scoped>
.sidebar-branded {
    background: linear-gradient(135deg, #26235A 0%, #E6038C 100%) !important;
    color: white !important;
}

.sidebar-branded :deep(.v-list-item),
.sidebar-branded :deep(.v-list-item__content),
.sidebar-branded :deep(.v-list-item-title),
.sidebar-branded :deep(.v-list-group__header),
.sidebar-branded :deep(.v-icon) {
    color: rgba(255, 255, 255, 0.85) !important;
}

.sidebar-branded :deep(.v-list-item:hover) {
    background: rgba(255, 255, 255, 0.12) !important;
}

.sidebar-branded :deep(.v-list-item--active) {
    background: rgba(255, 255, 255, 0.2) !important;
    border-left: 3px solid white;
}

.sidebar-branded :deep(.v-list-item--active .v-icon),
.sidebar-branded :deep(.v-list-item--active .v-list-item-title) {
    color: white !important;
}

.sidebar-branded :deep(.v-toolbar) {
    background: transparent !important;
}

.sidebar-branded :deep(.v-toolbar .v-btn) {
    color: rgba(255, 255, 255, 0.7) !important;
}

.sidebar-branded :deep(.v-divider) {
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.sidebar-branded :deep(.menu-locked) {
    opacity: 0.55;
}
</style>
